import { useInView } from '@/hooks/useInView';
import { ReactNode } from 'react';

interface FeatureCardProps {
  icon: (props: { className?: string }) => ReactNode;
  title: string;
  description: string;
  featured?: boolean;
  delay?: number;
}

export default function FeatureCard({
  icon: Icon,
  title,
  description,
  featured = false,
  delay = 0,
}: FeatureCardProps) {
  const { ref, isInView } = useInView(0.2);

  if (featured) {
    return (
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`col-span-full transition-all duration-700 ${
          isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="relative bg-gradient-to-br from-mk-pink-500 to-mk-pink-700 rounded-3xl p-8 md:p-12 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            {/* Icon */}
            <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center flex-shrink-0">
              <Icon className="w-12 h-12 text-white" />
            </div>

            {/* Content */}
            <div className="text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">{title}</h3>
              <p className="text-lg text-white/90 max-w-2xl">{description}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`transition-all duration-700 ${
        isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full border border-gray-100">
        {/* Icon */}
        <div className="w-14 h-14 bg-gradient-to-br from-mk-pink-100 to-mk-pink-200 rounded-2xl flex items-center justify-center mb-5">
          <Icon className="w-7 h-7 text-mk-pink-600" />
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-3">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
}
